import { changeLanguage } from "@i18n/i18n";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { AuthClientEvent } from "@react-keycloak/core/lib/types";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import { ReactNode } from "react";
import axios from "axios";

import keycloakClient from "../keycloak/keycloak";
import { ENVIRONMENT } from "../config";

export const options = {
  onLoad: "login-required"
};

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const handleKeycloakEvent = (event: AuthClientEvent) => {
    if (event === "onAuthSuccess") {
      // Set axios default headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${keycloakClient.token}`;
      axios.defaults.headers.common['Accept'] = 'application/json';

      // Get language from Keycloak ('es', 'en', 'ES', 'EN', etc.)
      const keycloakLocale = keycloakClient.tokenParsed?.locale;
      
      if (keycloakLocale) {
        const normalizedLang = keycloakLocale.toLowerCase();
        // Only change is if it is a supported language
        if (['en', 'es'].includes(normalizedLang)) {
          changeLanguage(normalizedLang);
          // Sync with localStorage to maintain consistency
          localStorage.setItem('selectedLanguage', normalizedLang.toUpperCase());
        }
      } else {
        // If Keycloak does not provide locale, keep the current language or the one from localStorage
        const storedLang = localStorage.getItem('selectedLanguage');
        if (storedLang) {
          changeLanguage(storedLang.toLowerCase());
        }
        // If there is no language saved, keep the default ('en')
      }
    }
  };

  const keycloakInitOptions = {
    ...options,
    ...(ENVIRONMENT === "local" ? { checkLoginIframe: false } : {})
  };

  return (
    <ReactKeycloakProvider
      authClient={keycloakClient}
      initOptions={keycloakInitOptions}
      onEvent={handleKeycloakEvent}
      LoadingComponent={
        <Box className="flex w-screen h-screen items-center justify-center">
          <CircularProgress />
        </Box>
      }
    >
      {children}
    </ReactKeycloakProvider>
  );
}
